import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { FiDownload, FiSearch, FiX } from "react-icons/fi";
import { userReportEndpoints } from "../../../services/apis";

const initialFilters = {
  search: "",
  contentKind: "",
  registeredFrom: "",
  registeredTo: "",
  purchaseFrom: "",
  purchaseTo: "",
};

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(value))
    : "—";

const formatMoney = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const SummaryCard = ({ label, value }) => (
  <div className="rounded-xl border border-richblack-600 bg-richblack-800 p-5">
    <p className="text-sm text-richblack-300">{label}</p>
    <p className="mt-2 text-2xl font-semibold text-richblack-5">{value}</p>
  </div>
);

const AdminUserReport = () => {
  const { token } = useSelector((state) => state.auth);
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );
  const displayRows = useMemo(
    () =>
      users.flatMap((user) =>
        user.purchases?.length
          ? user.purchases.map((purchase) => ({ user, purchase }))
          : [{ user, purchase: null }],
      ),
    [users],
  );

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const params = { page, limit: 20 };
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params[key] = value;
        });
        const response = await axios.get(userReportEndpoints.GET_USERS, {
          headers,
          params,
          signal: controller.signal,
        });
        setUsers(response.data?.data || []);
        setSummary(response.data?.summary || {});
        setPagination(response.data?.pagination || { page: 1, totalPages: 1, total: 0 });
      } catch (error) {
        if (error.code !== "ERR_CANCELED") {
          toast.error(error.response?.data?.message || "Could not load users");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, filters.search ? 350 : 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [filters, page, headers]);

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const openUser = async (user) => {
    try {
      setSelectedUser(user);
      setOrders([]);
      setDetailsLoading(true);
      const [detailResponse, ordersResponse] = await Promise.all([
        axios.get(`${userReportEndpoints.GET_USERS}/${user.id}`, { headers }),
        axios.get(`${userReportEndpoints.GET_USERS}/${user.id}/orders`, {
          headers,
          params: { page: 1, limit: 100 },
        }),
      ]);
      setSelectedUser(detailResponse.data?.data || user);
      setOrders(ordersResponse.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not load user details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const exportUsers = async () => {
    try {
      setExporting(true);
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const response = await axios.get(userReportEndpoints.EXPORT_USERS, {
        headers,
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `admin_users_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not export users");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="w-full p-2 text-richblack-5 md:p-6">
      <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-semibold">User Dashboard</h1>
          <p className="mt-1 text-richblack-300">
            Users, enrollments, purchases and invoice history.
          </p>
        </div>
        <button
          onClick={exportUsers}
          disabled={exporting}
          className="flex items-center justify-center gap-2 rounded-lg bg-yellow-100 px-5 py-3 font-semibold text-richblack-900 disabled:opacity-60"
        >
          <FiDownload /> {exporting ? "Exporting..." : "Download Excel"}
        </button>
      </div>

      <div className="mb-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard label="Total Users" value={summary.totalUsers || 0} />
        <SummaryCard label="Paid Customers" value={summary.paidCustomers || 0} />
        <SummaryCard label="Total Purchases" value={summary.totalPurchases || 0} />
        <SummaryCard label="Total Revenue" value={formatMoney(summary.totalRevenue)} />
      </div>

      <div className="mb-6 rounded-xl border border-richblack-600 bg-richblack-800 p-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="relative xl:col-span-2">
            <FiSearch className="absolute left-3 top-3.5 text-richblack-300" />
            <input
              value={filters.search}
              onChange={(event) => updateFilter("search", event.target.value)}
              placeholder="Search name or email"
              className="w-full rounded-lg border border-richblack-600 bg-richblack-700 py-3 pl-10 pr-3 text-richblack-5 outline-none focus:border-yellow-100"
            />
          </label>
          <select
            value={filters.contentKind}
            onChange={(event) => updateFilter("contentKind", event.target.value)}
            className="rounded-lg border border-richblack-600 bg-richblack-700 px-3 py-3 text-richblack-5"
          >
            <option value="">All content</option>
            <option value="course">Courses</option>
            <option value="program">Programs</option>
            <option value="mentorship">Mentorships</option>
          </select>
          <button
            onClick={() => {
              setFilters(initialFilters);
              setPage(1);
            }}
            className="rounded-lg border border-richblack-500 px-4 py-3 hover:bg-richblack-700"
          >
            Clear filters
          </button>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            ["registeredFrom", "Registered from"],
            ["registeredTo", "Registered to"],
            ["purchaseFrom", "Purchased from"],
            ["purchaseTo", "Purchased to"],
          ].map(([key, label]) => (
            <label key={key} className="text-xs text-richblack-300">
              {label}
              <input
                type="date"
                value={filters[key]}
                onChange={(event) => updateFilter(key, event.target.value)}
                className="mt-1 w-full rounded-lg border border-richblack-600 bg-richblack-700 px-3 py-2 text-richblack-5"
              />
            </label>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-richblack-600 bg-richblack-800">
        <div className="overflow-x-auto">
          <table className="min-w-[1900px] w-full text-left text-sm">
            <thead className="bg-richblack-700 text-richblack-200">
              <tr>
                {[
                  "Name",
                  "Email ID",
                  "Phone No",
                  "Registration Date",
                  "Address",
                  "State",
                  "Pincode",
                  "Course Purchased",
                  "Invoice Amount",
                  "Basic",
                  "GST",
                  "CGST",
                  "SGST",
                  "Total GST",
                  "Invoice Number",
                ].map((heading) => (
                  <th key={heading} className="px-4 py-3 font-medium">{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="15" className="px-4 py-12 text-center text-richblack-300">Loading users...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan="15" className="px-4 py-12 text-center text-richblack-300">No users found.</td></tr>
              ) : (
                displayRows.map(({ user, purchase }, index) => (
                  <tr key={`${user.id}-${purchase?.id || "none"}-${index}`} className="border-t border-richblack-600 hover:bg-richblack-700/60">
                    <td className="px-4 py-4">
                      <button onClick={() => openUser(user)} className="font-medium text-richblack-5 underline-offset-4 hover:text-yellow-100 hover:underline">
                        {user.name || "Unnamed user"}
                      </button>
                    </td>
                    <td className="max-w-[240px] truncate px-4 py-4">{user.email || "—"}</td>
                    <td className="px-4 py-4">{user.mobile || "—"}</td>
                    <td className="px-4 py-4">{formatDate(user.registeredAt)}</td>
                    <td className="max-w-[280px] px-4 py-4">{purchase?.address || user.billing?.addressLine1 || "—"}</td>
                    <td className="px-4 py-4">{purchase?.state || user.billing?.state || "—"}</td>
                    <td className="px-4 py-4">{purchase?.pincode || user.billing?.pincode || "—"}</td>
                    <td className="max-w-[260px] px-4 py-4">{purchase?.items?.join(", ") || "—"}</td>
                    <td className="px-4 py-4 font-medium">{purchase ? formatMoney(purchase.invoiceAmount) : "—"}</td>
                    <td className="px-4 py-4">{purchase ? formatMoney(purchase.basicAmount) : "—"}</td>
                    <td className="px-4 py-4">{purchase ? formatMoney(purchase.gst) : "—"}</td>
                    <td className="px-4 py-4">{purchase ? formatMoney(purchase.cgst) : "—"}</td>
                    <td className="px-4 py-4">{purchase ? formatMoney(purchase.sgst) : "—"}</td>
                    <td className="px-4 py-4 font-medium">{purchase ? formatMoney(purchase.totalGst) : "—"}</td>
                    <td className="px-4 py-4">
                      {purchase?.pdfUrl ? (
                        <a href={purchase.pdfUrl} target="_blank" rel="noreferrer" className="text-blue-200 underline">
                          {purchase.invoiceNumber}
                        </a>
                      ) : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-richblack-600 px-4 py-4 text-sm">
          <p className="text-richblack-300">{pagination.total || 0} users</p>
          <div className="flex items-center gap-3">
            <button
              disabled={page <= 1 || loading}
              onClick={() => setPage((value) => value - 1)}
              className="rounded border border-richblack-500 px-3 py-2 disabled:opacity-40"
            >Previous</button>
            <span>Page {pagination.page || page} of {pagination.totalPages || 1}</span>
            <button
              disabled={page >= (pagination.totalPages || 1) || loading}
              onClick={() => setPage((value) => value + 1)}
              className="rounded border border-richblack-500 px-3 py-2 disabled:opacity-40"
            >Next</button>
          </div>
        </div>
      </div>

      {selectedUser && (
        <div className="fixed inset-0 z-[1000] flex justify-end bg-richblack-900/75" onClick={() => setSelectedUser(null)}>
          <aside
            className="h-full w-full max-w-2xl overflow-y-auto bg-richblack-800 p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-semibold">{selectedUser.name || "User details"}</h2>
                <p className="text-richblack-300">{selectedUser.email}</p>
                <p className="text-richblack-300">{selectedUser.mobile || "No mobile number"}</p>
              </div>
              <button onClick={() => setSelectedUser(null)} className="rounded p-2 hover:bg-richblack-700" aria-label="Close"><FiX size={22} /></button>
            </div>

            {detailsLoading ? (
              <p className="py-16 text-center text-richblack-300">Loading details...</p>
            ) : (
              <>
                <div className="mt-6 grid gap-3 sm:grid-cols-3">
                  <SummaryCard label="Enrollments" value={selectedUser.enrollmentCount || 0} />
                  <SummaryCard label="Purchases" value={selectedUser.purchaseCount || 0} />
                  <SummaryCard label="Total Spent" value={formatMoney(selectedUser.totalSpent)} />
                </div>

                <section className="mt-7">
                  <h3 className="mb-3 text-lg font-semibold">Enrolled content</h3>
                  <div className="space-y-2">
                    {(selectedUser.enrollments || []).length === 0 ? (
                      <p className="text-richblack-300">No enrollments.</p>
                    ) : selectedUser.enrollments.map((item) => (
                      <div key={item.id} className="flex items-center justify-between rounded-lg border border-richblack-600 p-3">
                        <div><p>{item.name}</p><p className="text-xs capitalize text-richblack-300">{item.contentKind} · {item.courseType}</p></div>
                        <span>{formatMoney(item.price)}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="mt-7">
                  <h3 className="mb-3 text-lg font-semibold">Order history</h3>
                  <div className="space-y-3">
                    {orders.length === 0 ? (
                      <p className="text-richblack-300">No paid orders.</p>
                    ) : orders.map((order) => (
                      <div key={order.id} className="rounded-lg border border-richblack-600 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="font-medium">{order.items?.join(", ") || "Order"}</p>
                            <p className="text-xs text-richblack-300">{order.invoiceNumber} · {formatDate(order.purchasedAt)}</p>
                          </div>
                          <p className="font-semibold text-yellow-100">{formatMoney(order.amountPaid)}</p>
                        </div>
                        {order.pdfUrl && (
                          <a href={order.pdfUrl} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm text-blue-200 underline">
                            <FiDownload /> View invoice
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              </>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

export default AdminUserReport;
